import { Fragment, createElement, useCallback, useRef, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import useAppModal from '@/hooks/useAppModal';
import useAppToast from '@/hooks/useAppToast';
import { useVisitScheduleExistsCheckMutation } from '@/hooks/main/useVisitSchedule';
import { SERVICE_TYPE_A2 } from '@/constants/serviceType';
import type { VisitDraftPayload, VisitScheduleEvent } from '@/pages/main/visitSchedule/types';
import {
  checkInsurerSpecialCostOutOfRange,
  checkTreatmentIsExist,
  fetchPatientDayVisits,
} from '@/services/main/visitSchedule';
import { MSG } from '@/pages/main/visitSchedule/VisitSchedulePage.constants';
import { excludePendingDeletedVisits } from './VisitScheduleDuplicateChecks';
import { buildSingleDeleteSameAddressWarning } from './sameAddressDeleteWarning';

interface VisitScheduleCheckEvent {
  id: string | number;
  patient_id: number;
  massager_id: number;
  visit_datetime_from: string;
  is_locked?: boolean;
  is_suddenly_visit?: boolean;
  same_visit_id?: number | null;
}

interface VisitScheduleMassagerOption {
  id: number;
  available_from?: string | null;
  available_to?: string | null;
  massager: {
    id: number;
    serial_number: number | null;
    name: string;
  } | null;
}

interface UseVisitScheduleModalActionsParams {
  tenant: string;
  clinicNumber: string;
  serviceType: string;
  visit?: VisitScheduleEvent | null;
  isExisting: boolean;
  isSaving: boolean;
  patientId: number | undefined;
  visitDate: Dayjs;
  visitHour: string;
  visitMinute: string;
  isVisitTimeValid: boolean;
  visitMinutes: number | null;
  copyTargetDates: string[];
  isSuddenlyVisit: boolean;
  hospitalName: string;
  doctorName: string;
  visitReason: string;
  alignmentDate: string;
  existingEvents: VisitScheduleCheckEvent[];
  pendingDeletedVisitIds: number[];
  currentMassagerId?: number | null;
  massagers: VisitScheduleMassagerOption[];
  getCurrentPayload: () => VisitDraftPayload;
  onSave: (payload: VisitDraftPayload) => void;
  onDelete?: (id: string | number) => void;
  onCopyToOtherDays?: (payload: VisitDraftPayload, dates: string[]) => void;
  onClose: () => void;
  clearFormChange: () => void;
}

export function useVisitScheduleModalActions({
  tenant,
  clinicNumber,
  serviceType,
  visit,
  isExisting,
  isSaving,
  patientId,
  visitDate,
  visitHour,
  visitMinute,
  isVisitTimeValid,
  visitMinutes,
  copyTargetDates,
  isSuddenlyVisit,
  hospitalName,
  doctorName,
  visitReason,
  alignmentDate,
  existingEvents,
  pendingDeletedVisitIds,
  currentMassagerId,
  massagers,
  getCurrentPayload,
  onSave,
  onDelete,
  onCopyToOtherDays,
  onClose,
  clearFormChange,
}: UseVisitScheduleModalActionsParams) {
  const toast = useAppToast();
  const { error, warning, confirm } = useAppModal();
  const { checkVisitExists } = useVisitScheduleExistsCheckMutation();

  const [isCopying, setIsCopying] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const copySubmitLockRef = useRef(false);
  const deleteSubmitLockRef = useRef(false);
  const footerActionBusy = isCopying || isApplying || isSaving || isDeleting;

  const handleSave = useCallback(async () => {
    if (footerActionBusy) return;
    setIsApplying(true);
    try {
      // 1. 受診者未選択
      if (patientId == null || patientId === 0) {
        error({ title: '受診者を選択してください。', okText: 'OK' });
        return;
      }
      // 2. 日付未入力
      if (!visitDate.isValid()) {
        error({ title: '日付を入力してください。', okText: 'OK' });
        return;
      }
      // 3. 日付フォーマットエラー (visitTime も含む)
      if (!isVisitTimeValid) {
        error({ title: '日付を正しく入力してください。', okText: 'OK' });
        return;
      }
      // 4. 突発的な往療: 必須項目 (check before 所要時間 so required-field message takes priority)
      if (isSuddenlyVisit) {
        if (
          !hospitalName?.trim() ||
          !doctorName?.trim() ||
          !visitReason?.trim() ||
          !alignmentDate?.trim()
        ) {
          warning({ title: '必須項目を入力して下さい。' });
          return;
        }
        // 連携日フォーマットチェック
        if (!dayjs(alignmentDate).isValid()) {
          error({ title: '日付を正しく入力してください。', okText: 'OK' });
          return;
        }
      }
      // 5. 所要時間
      if (!visitMinutes || visitMinutes <= 0 || !Number.isInteger(visitMinutes)) {
        error({ title: '所要時間を正しく入力してください。', okText: 'OK' });
        return;
      }
      // 6. 日付をまたいだ施術日チェック
      const from = visitDate
        .clone()
        .hour(Number(visitHour) || 0)
        .minute(Number(visitMinute) || 0)
        .second(0)
        .millisecond(0);
      const to = from.add(visitMinutes, 'minute');
      if (from.format('YYYY-MM-DD') !== to.format('YYYY-MM-DD')) {
        toast.error('日付をまたいだ施術日は登録できません');
        return;
      }
      // 6.1 施術師の適用期限チェック — visit_date が available_from/available_to の範囲外なら拒否。
      if (currentMassagerId != null) {
        const currentMassager = (massagers ?? []).find(m => m.massager?.id === currentMassagerId);
        if (currentMassager) {
          const vDate = from.format('YYYY-MM-DD');
          const mFrom = currentMassager.available_from?.trim() || null;
          const mTo = currentMassager.available_to?.trim() || null;
          if ((mFrom && vDate < mFrom) || (mTo && vDate > mTo)) {
            toast.error('施術師の適用期限外のため登録できません。');
            return;
          }
        }
      }
      // 5.1 Duplicate / locked patient checks (BD)
      // existingEvents only contains visits of the currently-selected 施術師 (calendarVisits
      // is scoped to massagerId). To detect cross-massager duplicates (RA2: BE alerted via
      // AJAX), fetch all visits for this patient on this date, then merge by id.
      const visitDateStr = from.format('YYYY-MM-DD');
      const visitMonthStr = from.format('YYYY-MM');
      const editingId = visit?.id;
      let crossMassagerEvents: VisitScheduleCheckEvent[] = [];
      if (patientId != null) {
        try {
          const monthsToQuery = Array.from(
            new Set([
              from.subtract(14, 'day').format('YYYY-MM'),
              visitMonthStr,
              from.add(14, 'day').format('YYYY-MM'),
            ])
          );
          const remoteByMonth = await Promise.all(
            monthsToQuery.map(month =>
              fetchPatientDayVisits(tenant, clinicNumber, patientId, visitDateStr, month, serviceType)
            )
          );
          crossMassagerEvents = remoteByMonth.flat().map(row => ({
            id: row.id,
            patient_id: row.patient_id,
            massager_id: row.massager_id,
            visit_datetime_from: row.visit_datetime_from,
            is_locked: row.is_locked,
            is_suddenly_visit: row.is_suddenly_visit,
          }));
        } catch {
          // API error — fall back to FE-only check (status quo pre-fix)
        }
      }
      const existingIds = new Set(existingEvents.map(event => event.id));
      const mergedEvents = [
        ...existingEvents,
        ...crossMassagerEvents.filter(event => !existingIds.has(event.id)),
      ];
      const activeEvents = excludePendingDeletedVisits(mergedEvents, pendingDeletedVisitIds);
      const samePatientEvents = activeEvents.filter(
        event => event.patient_id === patientId && event.id !== editingId
      );
      // Block for both create and edit when the target month already has a locked visit
      // on the same patient.
      const monthLocked = samePatientEvents.some(
        event => !!event.is_locked && (event.visit_datetime_from ?? '').slice(0, 7) === visitMonthStr
      );
      if (monthLocked) {
        error({ title: 'この受診者はロックされています。追加はできません。', okText: 'OK' });
        return;
      }
      if (copyTargetDates.length > 0) {
        try {
          const exists = await checkTreatmentIsExist(tenant, clinicNumber, patientId, copyTargetDates);
          if (exists) {
            error({ title: MSG.copy_target_has_treatment, okText: 'OK' });
            return;
          }
        } catch {
          // API error — skip check, allow legacy validations below
        }
      }
      const sameDayEvents = samePatientEvents.filter(
        event => (event.visit_datetime_from ?? '').slice(0, 10) === visitDateStr
      );

      if (sameDayEvents.some(event => event.massager_id === currentMassagerId)) {
        warning({ title: 'この受診者はすでに登録されています。', okText: 'OK' });
        return;
      }
      if (sameDayEvents.length > 0) {
        warning({ title: 'この受診者はすでに他の施術師にて登録されています。', okText: 'OK' });
        return;
      }
      // 6. 保険者適用期間チェック (VisitErrorChecker [10b] parity)
      try {
        const outOfRange = await checkInsurerSpecialCostOutOfRange(
          tenant,
          clinicNumber,
          patientId,
          visitDateStr
        );
        if (outOfRange.outOfRange) {
          error({
            title: outOfRange.message ?? '保険者の適用期間を確認してください。',
            okText: 'OK',
          });
          return;
        }
      } catch {
        // API error — skip check, allow save
      }

      // 7. 来院チェック (treatment_is_exist) — A2 only
      if (serviceType === SERVICE_TYPE_A2) {
        try {
          const visitDates = [from.format('YYYY-MM-DD')];
          copyTargetDates.forEach(date => {
            if (!visitDates.includes(date)) visitDates.push(date);
          });
          const exists = await checkTreatmentIsExist(tenant, clinicNumber, patientId, visitDates);
          if (exists) {
            error({ title: 'この受診者は来院の施術があります。', okText: 'OK' });
            return;
          }
        } catch {
          // API error — skip check, allow save
        }
      }
      // 8. Address date range (getBetweenDays) — removed (HIT-6079): the patient search now
      // returns patients whose 受診者情報 history does not cover the visit date, and the
      // visit is saved and flagged 「施術日に対応する受診者情報の登録がありません。」 instead.
      // 8. 突発的な往療: 重複チェック (必須項目/日付フォーマットは既に step 4 で検証済み)
      if (isSuddenlyVisit) {
        // UAT: same patient + +/-14 days (same or different doctor) -> block.
        // editingId has been removed from samePatientEvents, so it is not compared against the visit being edited.
        const conflict = samePatientEvents.find(event => {
          if (!event.is_suddenly_visit) return false;
          const eventDate = dayjs((event.visit_datetime_from ?? '').slice(0, 10));
          if (!eventDate.isValid()) return false;
          return Math.abs(eventDate.diff(from, 'day')) <= 14;
        });

        if (conflict) {
          const massager = massagers.find(m => m.massager?.id === conflict.massager_id);
          const serial = massager?.massager?.serial_number ?? conflict.massager_id;
          const dateStr = dayjs(conflict.visit_datetime_from).format('YYYY/MM/DD');
          error({
            title: `突発的な往療は前後14日以内で複数登録できません\n施術師:${serial}  施術日${dateStr}で既に登録されています`,
            okText: 'OK',
          });
          return;
        }
        // 11. 突発的な往療: same patient + same doctor + same month + different day → warning only
        const sameMonthSameMassager = samePatientEvents.some(event => {
          if (!event.is_suddenly_visit) return false;
          if (event.massager_id !== currentMassagerId) return false;
          const date = (event.visit_datetime_from ?? '').slice(0, 10);
          return date.slice(0, 7) === from.format('YYYY-MM') && date !== from.format('YYYY-MM-DD');
        });
        if (sameMonthSameMassager) {
          const confirmed = await new Promise<boolean>(resolve => {
            confirm({
              title: '確認',
              content:
                '月内に２回以上、突発的な往療を登録した場合は、レセプト摘要欄に「突発往療理由」、「医療機関名」、「医師名」、「連携日」を手書きで記載ください。',
              okText: 'OK',
              cancelText: 'キャンセル',
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          });
          if (!confirmed) return;
        }
      }
      // Stale-data guard: if editing an existing (DB-saved) visit, verify it
      // still exists before applying to local state. `isExisting` = visit has a
      // numeric id, meaning it was loaded from DB (drafts have string ids).
      if (isExisting && typeof visit?.id === 'number') {
        try {
          await checkVisitExists(visit.id);
        } catch (err: unknown) {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 404) {
            toast.error('エラーが発生しました。しばらくしてから再度お試しください。');
            return;
          }
          // Non-404 (network / 5xx): allow apply; submit-total will re-validate.
        }
      }

      onSave(getCurrentPayload());
      clearFormChange();
      onClose();
    } finally {
      setIsApplying(false);
    }
  }, [
    tenant,
    clinicNumber,
    serviceType,
    patientId,
    visitDate,
    visitHour,
    visitMinute,
    isVisitTimeValid,
    visitMinutes,
    copyTargetDates,
    getCurrentPayload,
    onSave,
    onClose,
    isExisting,
    checkVisitExists,
    isSuddenlyVisit,
    hospitalName,
    doctorName,
    visitReason,
    alignmentDate,
    toast,
    error,
    warning,
    confirm,
    existingEvents,
    pendingDeletedVisitIds,
    currentMassagerId,
    visit?.id,
    massagers,
    clearFormChange,
    footerActionBusy,
  ]);

  const handleDelete = useCallback(() => {
    if (footerActionBusy || deleteSubmitLockRef.current) return;
    deleteSubmitLockRef.current = true;
    setIsDeleting(true);
    try {
      if (visit?.id == null || !onDelete) return;
      if (visit.is_locked) {
        toast.error('ロックされているため、編集及び削除はできません。');
        return;
      }

      const runDelete = () => {
        onDelete(visit.id);
        clearFormChange();
        onClose();
      };
      const sameAddressWarning = buildSingleDeleteSameAddressWarning(
        visit,
        existingEvents,
        pendingDeletedVisitIds
      );
      if (sameAddressWarning) {
        confirm({
          title: '確認',
          content: createElement(
            Fragment,
            null,
            MSG.delete_same_address_warn,
            createElement('br'),
            `削除前${sameAddressWarning.before}人　→　削除後${sameAddressWarning.after}人`
          ),
          okText: 'OK',
          cancelText: 'キャンセル',
          onOk: runDelete,
        });
        return;
      }

      runDelete();
    } finally {
      deleteSubmitLockRef.current = false;
      setIsDeleting(false);
    }
  }, [
    visit,
    onDelete,
    onClose,
    toast,
    clearFormChange,
    footerActionBusy,
    existingEvents,
    pendingDeletedVisitIds,
    confirm,
  ]);

  const handleCopy = useCallback(async () => {
    if (footerActionBusy || copySubmitLockRef.current) return;
    copySubmitLockRef.current = true;
    setIsCopying(true);
    try {
      // 1. Check if user has changed the date (compare with original visit)
      const currentDateStr = visitDate.format('YYYY-MM-DD');
      const originalDateStr = visit?.visit_datetime_from
        ? dayjs(visit.visit_datetime_from).format('YYYY-MM-DD')
        : '';
      if (currentDateStr === originalDateStr) {
        error({ title: 'コピーする訪問日時を指定してください。', okText: 'OK' });
        return;
      }
      // 2. Patient selection required
      if (patientId == null || patientId === 0) {
        error({ title: '受診者を選択してください。', okText: 'OK' });
        return;
      }
      // 3. Visit date required
      if (!visitDate.isValid()) {
        error({ title: '日付を入力してください。', okText: 'OK' });
        return;
      }
      // 4. Visit time format
      if (!isVisitTimeValid) {
        error({ title: '日付を正しく入力してください。', okText: 'OK' });
        return;
      }
      // 5. Visit minutes
      if (!visitMinutes || visitMinutes <= 0 || !Number.isInteger(visitMinutes)) {
        error({ title: '所要時間を正しく入力してください。', okText: 'OK' });
        return;
      }
      // 5.1 施術師の適用期限チェック (copy target date — same rule as handleSave step 6.1)
      if (currentMassagerId != null) {
        const currentMassager = (massagers ?? []).find(m => m.massager?.id === currentMassagerId);
        if (currentMassager) {
          const vDate = visitDate.format('YYYY-MM-DD');
          const mFrom = currentMassager.available_from?.trim() || null;
          const mTo = currentMassager.available_to?.trim() || null;
          if ((mFrom && vDate < mFrom) || (mTo && vDate > mTo)) {
            toast.error('施術師の適用期限外のためコピーできません。');
            return;
          }
        }
      }
      // 6. Local duplicate / locked patient checks on target date (match handleSave)
      const from = visitDate
        .clone()
        .hour(Number(visitHour) || 0)
        .minute(Number(visitMinute) || 0)
        .second(0)
        .millisecond(0);
      const visitDateStr = from.format('YYYY-MM-DD');
      const visitMonthStr = visitDate.format('YYYY-MM');
      const editingId = visit?.id;
      let crossMassagerEvents: VisitScheduleCheckEvent[] = [];
      if (patientId != null) {
        try {
          const monthsToQuery = Array.from(
            new Set([
              from.subtract(14, 'day').format('YYYY-MM'),
              visitMonthStr,
              from.add(14, 'day').format('YYYY-MM'),
            ])
          );
          const remoteByMonth = await Promise.all(
            monthsToQuery.map(month =>
              fetchPatientDayVisits(tenant, clinicNumber, patientId, visitDateStr, month, serviceType)
            )
          );
          crossMassagerEvents = remoteByMonth.flat().map(row => ({
            id: row.id,
            patient_id: row.patient_id,
            massager_id: row.massager_id,
            visit_datetime_from: row.visit_datetime_from,
            is_locked: row.is_locked,
            is_suddenly_visit: row.is_suddenly_visit,
          }));
        } catch {
          // API error — fall back to FE-only check
        }
      }
      const existingIds = new Set(existingEvents.map(event => event.id));
      const mergedEvents = [
        ...existingEvents,
        ...crossMassagerEvents.filter(event => !existingIds.has(event.id)),
      ];
      const activeEvents = excludePendingDeletedVisits(mergedEvents, pendingDeletedVisitIds);
      const samePatientEvents = activeEvents.filter(
        event => event.patient_id === patientId && event.id !== editingId
      );
      const monthLocked = samePatientEvents.some(
        event => !!event.is_locked && (event.visit_datetime_from ?? '').slice(0, 7) === visitMonthStr
      );
      if (monthLocked) {
        error({ title: 'この受診者はロックされています。追加はできません。', okText: 'OK' });
        return;
      }

      // Copy must be blocked with the dedicated 来院 message before the generic
      // same-patient duplicate warning.
      try {
        const exists = await checkTreatmentIsExist(tenant, clinicNumber, patientId, [
          currentDateStr,
        ]);
        if (exists) {
          error({ title: MSG.copy_target_has_treatment, okText: 'OK' });
          return;
        }
      } catch {
        // API error — skip check, allow legacy validations below
      }

      const sameDayEvents = samePatientEvents.filter(
        event => (event.visit_datetime_from ?? '').slice(0, 10) === currentDateStr
      );
      if (sameDayEvents.some(event => event.massager_id === currentMassagerId)) {
        warning({ title: 'この受診者はすでに登録されています。', okText: 'OK' });
        return;
      }
      if (sameDayEvents.length > 0) {
        warning({ title: 'この受診者はすでに他の施術師にて登録されています。', okText: 'OK' });
        return;
      }
      if (onCopyToOtherDays) {
        onCopyToOtherDays(getCurrentPayload(), [currentDateStr]);
        clearFormChange();
        onClose();
      }
    } finally {
      copySubmitLockRef.current = false;
      setIsCopying(false);
    }
  }, [
    visitDate,
    visit?.id,
    visit?.visit_datetime_from,
    patientId,
    visitHour,
    visitMinute,
    isVisitTimeValid,
    visitMinutes,
    tenant,
    clinicNumber,
    getCurrentPayload,
    onCopyToOtherDays,
    onClose,
    toast,
    error,
    warning,
    existingEvents,
    pendingDeletedVisitIds,
    currentMassagerId,
    massagers,
    clearFormChange,
    footerActionBusy,
  ]);

  return {
    footerActionBusy,
    handleSave,
    handleDelete,
    handleCopy,
    isCopying,
    isApplying,
    isDeleting,
  };
}
